import { useCallback, useEffect, useMemo, useState } from 'react';
import 'react-quill-new/dist/quill.bubble.css';
import {
  Autocomplete,
  Checkbox,
  Chip,
  FormControl,
  ListItemText,
  MenuItem,
  Select,
  Stack,
  TextField,
  Tooltip,
} from '@mui/material';
import type { SelectChangeEvent } from '@mui/material';
import { CardContainer } from 'src/components/atoms';
import { ErrorMessage } from 'src/components/atoms/FormErrorMessage';
import { DefaultOption } from 'src/components/molecules/DropdownSelect/components/DropdownOptions';
import { FormType } from 'src/components/pages/CoiFormManagement/types';
import { filterCategoriesEligibleForNewForm } from 'src/components/pages/FormCreation/components/CategorySelection/helpers/filterCategoriesEligibleForNewForm';
import {
  CategoryType,
  useCountriesQuery,
  useGetOrganisationCategoriesQuery,
} from 'src/graphql';
import { LanguageOption } from 'src/utils/languages';
import { validateCategoryName } from '../../helpers/validation';
import { useFormType } from '../../hooks';
import { CountryOption } from '../../types';
import { CoiCategoryExpandedFields } from './CoiCategoryExpandedFields';
import { InfoTooltip } from './InfoTooltip';
import {
  CategoryConfigurationExpanded,
  CategoryNameDropdownWrapper,
  CategoryPanelHeader,
  CategoryPanelTitle,
  ConfigContent,
  CountryCard,
  CountryLabel,
  DuplicateManagementCategorySurface,
  FormField,
  FormLabel,
  FormSection,
  RequiredText,
} from './Configuration.styles';
import {
  createHandleCategorySelect,
  getCategoriesListForConfiguration,
  isSameCategoryName,
  transformSpeakUpCategories,
} from './helpers';
import { LanguagesSection } from './LanguagesSection';
import { SpeakUpCategoryExpandedFields } from './SpeakUpCategoryExpandedFields';

const NO_CATEGORIES_PLACEHOLDER_VALUE = '__form_creation_no_categories__';
const GLOBAL_COUNTRY_VALUE = 'GLOBAL';
const GLOBAL_COUNTRY_OPTION: CountryOption = {
  label: 'Global',
  value: GLOBAL_COUNTRY_VALUE,
};

interface Props {
  selectedLanguages: LanguageOption[];
  setSelectedLanguages: (languages: LanguageOption[]) => void;
  categoryName: string;
  setCategoryName: (value: string) => void;
  setCategoryId: (value: number | null) => void;
  categoryNameTouched: boolean;
  setCategoryNameTouched: (value: boolean) => void;
  categoryDescription: string;
  setCategoryDescription: (value: string) => void;
  categoryDescriptionTouched: boolean;
  setCategoryDescriptionTouched: (value: boolean) => void;
  selectedCountry: CountryOption[];
  setSelectedCountry: (value: CountryOption[]) => void;
  countryTouched: boolean;
  setCountryTouched: (value: boolean) => void;
  autoReviewEnabled: boolean;
  setAutoReviewEnabled: (value: boolean) => void;
  onSpeakUpCategoryChosenInConfig?: () => void;
  isEditMode: boolean;
  isDuplicateManagementDraft?: boolean;
}

export const Configuration = ({
  selectedLanguages,
  setSelectedLanguages,
  categoryName,
  setCategoryName,
  setCategoryId,
  categoryNameTouched,
  setCategoryNameTouched,
  setCategoryDescription,
  selectedCountry,
  setSelectedCountry,
  countryTouched,
  setCountryTouched,
  autoReviewEnabled,
  setAutoReviewEnabled,
  onSpeakUpCategoryChosenInConfig,
  isEditMode,
  isDuplicateManagementDraft = false,
}: Props) => {
  const [hasChosenCategoryFromDropdown, setHasChosenCategoryFromDropdown] =
    useState(false);
  const [isDeterminedCategory, setIsDeterminedCategory] = useState(false);
  const [isReportedCategory, setIsReportedCategory] = useState(false);
  const [isExistingCategorySelected, setIsExistingCategorySelected] =
    useState(false);
  const [intakeChannelReportingPortal, setIntakeChannelReportingPortal] =
    useState(false);
  const [intakeChannelGoTogether, setIntakeChannelGoTogether] = useState(false);
  const formType = useFormType();
  const isSpeakUp = formType === FormType.SPEAK_UP;
  const { data: countriesData } = useCountriesQuery(undefined, {
    enabled: !isSpeakUp,
    staleTime: 5 * 60 * 1000, // Cache for 5 minutes
  });

  const countryOptions = useMemo<CountryOption[]>(
    () => [
      GLOBAL_COUNTRY_OPTION,
      ...(countriesData?.countries ?? []).map((country) => ({
        label: country.name,
        value: country.code.toUpperCase(),
      })),
    ],
    [countriesData],
  );
  const isGlobalCountrySelected = selectedCountry.some(
    (country) => country.value === GLOBAL_COUNTRY_VALUE,
  );
  const normalizedSelectedCountry = useMemo(
    () =>
      selectedCountry.map((selected) => {
        const matchedOption = countryOptions.find(
          (option) => option.value === selected.value,
        );
        return matchedOption ?? selected;
      }),
    [countryOptions, selectedCountry],
  );

  const { data: organisationCategoriesData } =
    useGetOrganisationCategoriesQuery(
      { isCOI: !isSpeakUp },
      { refetchOnWindowFocus: true },
    );

  const configurationApiCategories = useMemo(
    () =>
      transformSpeakUpCategories(
        filterCategoriesEligibleForNewForm(
          organisationCategoriesData?.getOrganisationCategories as
            | CategoryType[]
            | null
            | undefined,
          formType === FormType.CONFLICT_OF_INTEREST,
        ),
      ),
    [organisationCategoriesData, formType],
  );

  const configurationCategories = useMemo(
    () => getCategoriesListForConfiguration(configurationApiCategories),
    [configurationApiCategories],
  );

  const categoryDropdownOptions: DefaultOption[] = useMemo(() => {
    const mapped = configurationCategories.map((c) => ({
      value: c.id,
      label: c.name,
    }));
    if (mapped.length === 0) {
      return [
        {
          value: NO_CATEGORIES_PLACEHOLDER_VALUE,
          label: 'No categories available',
          isDisabled: true,
        } as DefaultOption & { isDisabled: boolean },
      ];
    }
    return mapped;
  }, [configurationCategories]);

  const selectedCategoryOption: DefaultOption | null = useMemo(() => {
    if (!categoryName) return null;
    const match = configurationCategories.find((c) =>
      isSameCategoryName(c.name, categoryName),
    );
    return match ? { value: match.id, label: match.name } : null;
  }, [categoryName, configurationCategories]);

  const selectedCategoryIntake = useMemo(() => {
    if (!categoryName) return null;
    if (selectedCategoryOption) {
      const byId = configurationApiCategories.find(
        (c) => c.id === selectedCategoryOption.value,
      );
      if (byId) return byId;
    }
    return (
      configurationApiCategories.find((c) =>
        isSameCategoryName(c.name, categoryName),
      ) ?? null
    );
  }, [categoryName, configurationApiCategories, selectedCategoryOption]);

  useEffect(() => {
    if (!categoryName) {
      setIntakeChannelReportingPortal(false);
      if (isSpeakUp) {
        setIntakeChannelGoTogether(false);
      }
      return;
    }
    if (!selectedCategoryIntake) {
      return;
    }
    setIntakeChannelReportingPortal(!!selectedCategoryIntake.displayInApp);
    if (isSpeakUp) {
      setIntakeChannelGoTogether(!!selectedCategoryIntake.allowGoTogether);
    }
  }, [isSpeakUp, categoryName, selectedCategoryIntake]);

  useEffect(() => {
    if (
      !isSpeakUp ||
      !categoryName ||
      configurationApiCategories.length === 0
    ) {
      return;
    }
    const matchingCategory = configurationApiCategories.find((cat) =>
      isSameCategoryName(cat.name, categoryName),
    );
    if (!matchingCategory) {
      return;
    }
    setIsDeterminedCategory(matchingCategory.isDeterminedCategory);
    setIsReportedCategory(matchingCategory.isReportedCategory);
  }, [isSpeakUp, categoryName, configurationApiCategories]);

  useEffect(() => {
    if (!categoryName) {
      setIsExistingCategorySelected(false);
    } else {
      setIsExistingCategorySelected(
        configurationCategories.some((c) =>
          isSameCategoryName(c.name, categoryName),
        ),
      );
    }
  }, [categoryName, configurationCategories]);

  useEffect(() => {
    if (!categoryName.trim()) {
      setHasChosenCategoryFromDropdown(false);
    }
  }, [categoryName]);

  const categoryNameError = useMemo(() => {
    if (!categoryNameTouched) return null;
    return validateCategoryName(categoryName);
  }, [categoryName, categoryNameTouched]);

  const countryError = useMemo(() => {
    if (!countryTouched || isSpeakUp) return null;
    return selectedCountry.length === 0
      ? 'Please select at least one country'
      : null;
  }, [selectedCountry, countryTouched, isSpeakUp]);

  const showCategoryConfigurationFields = isSpeakUp
    ? Boolean(
        hasChosenCategoryFromDropdown ||
        (categoryName.trim() !== '' && selectedCategoryOption),
      )
    : isEditMode || categoryName.trim() !== '' || hasChosenCategoryFromDropdown;

  const baseCategorySelect = useMemo(
    () =>
      createHandleCategorySelect(
        configurationApiCategories,
        isSpeakUp,
        setCategoryName,
        setCategoryDescription,
        setIsDeterminedCategory,
        setIsReportedCategory,
        setCategoryNameTouched,
        categoryNameTouched,
      ),
    [
      isSpeakUp,
      configurationApiCategories,
      setCategoryName,
      setCategoryDescription,
      setCategoryNameTouched,
      categoryNameTouched,
    ],
  );

  const handleCategorySelect = useCallback(
    (categoryId: number) => {
      setHasChosenCategoryFromDropdown(true);
      setCategoryId(categoryId);
      baseCategorySelect(categoryId);
      const hasEnglish = selectedLanguages.some(
        (lang) => lang.value === 'en-GB',
      );
      if (!hasEnglish) {
        setSelectedLanguages([
          ...selectedLanguages,
          { label: 'English', value: 'en-GB', isDefault: true },
        ]);
      } else {
        setSelectedLanguages(
          selectedLanguages.map((lang) =>
            lang.value === 'en-GB' ? { ...lang, isDefault: true } : lang,
          ),
        );
      }
      if (onSpeakUpCategoryChosenInConfig) {
        onSpeakUpCategoryChosenInConfig();
      }
    },
    [
      baseCategorySelect,
      onSpeakUpCategoryChosenInConfig,
      selectedLanguages,
      setCategoryId,
      setSelectedLanguages,
    ],
  );

  const categoryPanelInner = (
    <ConfigContent data-testid="configuration-content">
      <CategoryPanelHeader>
        <CategoryPanelTitle
          data-testid="configuration-title"
          style={{ display: 'flex', alignItems: 'center', gap: '8px' }}
        >
          Configure form
          <InfoTooltip
            tooltip="Some settings are inherited from the category you select. To change these, go to the Category configuration page."
            ariaLabel="Information about form configuration"
            infoButtonDataTestId="configuration-info-button"
            tooltipDataTestId="configuration-info-tooltip"
            width="400px"
          />
        </CategoryPanelTitle>
      </CategoryPanelHeader>

      <FormField>
        <FormLabel data-testid="category-name-label">
          Category name <RequiredText>*</RequiredText>
        </FormLabel>
        <CategoryNameDropdownWrapper>
          <FormControl fullWidth error={!!categoryNameError}>
            <Select
              displayEmpty
              value={selectedCategoryOption?.value?.toString() ?? ''}
              onChange={(e: SelectChangeEvent<string>) => {
                const raw = e.target.value;
                if (!raw || raw === '') return;
                if (raw === NO_CATEGORIES_PLACEHOLDER_VALUE) return;
                const id = Number(raw);
                if (Number.isFinite(id)) {
                  handleCategorySelect(id);
                }
              }}
              data-testid="category-name-input"
              inputProps={{ 'aria-label': 'Select category' }}
              renderValue={(selected) => {
                if (!selected) {
                  return (
                    <span style={{ color: 'inherit', opacity: 0.5 }}>
                      Select category
                    </span>
                  );
                }
                const opt = categoryDropdownOptions.find(
                  (o) => o.value?.toString() === selected,
                );
                return opt?.label ?? selected;
              }}
            >
              {categoryDropdownOptions.map((opt) => (
                <MenuItem
                  key={opt.value?.toString()}
                  value={opt.value?.toString() ?? ''}
                  disabled={
                    'isDisabled' in opt &&
                    (opt as DefaultOption & { isDisabled?: boolean }).isDisabled
                  }
                >
                  <Tooltip title={opt.label} placement="right" enterDelay={500}>
                    <ListItemText
                      primary={opt.label}
                      slotProps={{
                        primary: {
                          sx: {
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                          },
                        },
                      }}
                    />
                  </Tooltip>
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </CategoryNameDropdownWrapper>
        {categoryNameError && (
          <ErrorMessage
            id="category-name-error"
            data-testid="category-name-error"
          >
            {categoryNameError}
          </ErrorMessage>
        )}
      </FormField>

      {showCategoryConfigurationFields && (
        <CategoryConfigurationExpanded data-testid="category-configuration-expanded-fields">
          {isSpeakUp ? (
            <SpeakUpCategoryExpandedFields
              isExistingCategorySelected={isExistingCategorySelected}
              isDeterminedCategory={isDeterminedCategory}
              setIsDeterminedCategory={setIsDeterminedCategory}
              isReportedCategory={isReportedCategory}
              setIsReportedCategory={setIsReportedCategory}
              intakeChannelReportingPortal={intakeChannelReportingPortal}
              setIntakeChannelReportingPortal={setIntakeChannelReportingPortal}
              intakeChannelGoTogether={intakeChannelGoTogether}
              setIntakeChannelGoTogether={setIntakeChannelGoTogether}
            />
          ) : (
            <CoiCategoryExpandedFields
              autoReview={autoReviewEnabled}
              setAutoReview={setAutoReviewEnabled}
              intakeChannelReportingPortal={intakeChannelReportingPortal}
              setIntakeChannelReportingPortal={setIntakeChannelReportingPortal}
            />
          )}

          <CountryCard data-testid="country-and-languages-card">
            {!isSpeakUp && (
              <FormSection data-testid="country-section">
                <CountryLabel>
                  Countries <RequiredText>*</RequiredText>
                </CountryLabel>
                <Autocomplete
                  multiple
                  disableCloseOnSelect
                  fullWidth
                  options={countryOptions}
                  value={normalizedSelectedCountry}
                  getOptionLabel={(option) => option.label}
                  isOptionEqualToValue={(option, value) =>
                    option.value === value.value
                  }
                  getOptionDisabled={(option) =>
                    isGlobalCountrySelected &&
                    option.value !== GLOBAL_COUNTRY_VALUE
                  }
                  onChange={(_event, newValue) => {
                    const nextSelected = newValue.filter(
                      Boolean,
                    ) as CountryOption[];
                    const hasGlobal = nextSelected.some(
                      (country) => country.value === GLOBAL_COUNTRY_VALUE,
                    );
                    setSelectedCountry(
                      hasGlobal ? [GLOBAL_COUNTRY_OPTION] : nextSelected,
                    );
                    if (!countryTouched) {
                      setCountryTouched(true);
                    }
                  }}
                  renderOption={(props, option, { selected }) => {
                    const { key, ...optionProps } = props;
                    return (
                      <li
                        key={key}
                        {...optionProps}
                        data-testid={`${option.label}-select`}
                      >
                        <Checkbox
                          checked={selected}
                          style={{ marginRight: 8 }}
                          data-testid={`multi-select-option-${option.label}`}
                        />
                        {option.label}
                      </li>
                    );
                  }}
                  renderTags={() => null}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      placeholder="Add country"
                      error={!!countryError}
                      inputProps={{
                        ...params.inputProps,
                        'data-testid': 'country-dropdown',
                        'aria-label': 'Select country',
                      }}
                    />
                  )}
                  data-testid="country-dropdown"
                />
                {normalizedSelectedCountry.length > 0 && (
                  <Stack direction="row" flexWrap="wrap" gap={0.5} mt={1}>
                    {normalizedSelectedCountry.map((option, index) => (
                      <Chip
                        key={option.value}
                        label={option.label}
                        size="small"
                        variant="outlined"
                        onDelete={() => {
                          const next = normalizedSelectedCountry.filter(
                            (_, i) => i !== index,
                          );
                          setSelectedCountry(next);
                          if (!countryTouched) setCountryTouched(true);
                        }}
                      />
                    ))}
                  </Stack>
                )}
                {countryError && (
                  <ErrorMessage id="country-error" data-testid="country-error">
                    {countryError}
                  </ErrorMessage>
                )}
              </FormSection>
            )}

            <LanguagesSection
              selectedLanguages={selectedLanguages}
              setSelectedLanguages={setSelectedLanguages}
            />
          </CountryCard>
        </CategoryConfigurationExpanded>
      )}
    </ConfigContent>
  );

  return (
    <>
      {isDuplicateManagementDraft ? (
        <DuplicateManagementCategorySurface data-testid="configuration-section">
          {categoryPanelInner}
        </DuplicateManagementCategorySurface>
      ) : (
        <CardContainer hasShadow={false} data-testid="configuration-section">
          {categoryPanelInner}
        </CardContainer>
      )}
    </>
  );
};
